/* FocusPod homepage films only. The configurator engine, pricing, forms and AU
 * media are untouched. Lazy muted autoplay, with persistent manual pause. */
(() => {
  'use strict';
  const initialized = new WeakSet();
  const players = new Set();
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const smallScreen = window.matchMedia('(max-width: 700px)');
  const connection = navigator.connection;
  let pageActive = true;

  function attach(video, button) {
    if (initialized.has(video)) return;
    initialized.add(video);
    const label = button.querySelector('[data-fpm-video-label]');
    const icon = button.querySelector('[aria-hidden="true"]');
    const preview = video.dataset.fpmVideo === 'config';
    const playLabel = button.dataset.fpmPlayLabel || (preview ? 'Play preview' : 'Play product film');
    const pauseLabel = button.dataset.fpmPauseLabel || (preview ? 'Pause preview' : 'Pause product film');
    let posterReady = preview || !video.poster;
    let visible = false;
    let choice = null; // null: automatic, true: visitor played, false: visitor paused.
    let blocked = false;
    let pending = false;
    let disposed = false;
    let observer = null;
    let generation = 0;

    video.defaultMuted = true;
    video.muted = true;
    video.playsInline = true;
    video.loop = true;
    video.controls = false;
    // The controller owns playback, so reduced-motion, offscreen and user-pause
    // decisions cannot be undone by native autoplay when a source arrives.
    video.autoplay = false;
    button.hidden = false;
    if (!video.id) video.id = preview ? 'fp-home-config-film' : 'fp-home-hero-film';
    button.setAttribute('aria-controls', video.id);

    const wantsPlayback = () => !disposed && pageActive && !document.hidden && visible &&
      choice !== false && (choice === true || (posterReady && !motion.matches && !connection?.saveData));

    function sync() {
      const playing = !video.paused && !video.ended;
      const text = playing ? pauseLabel : playLabel;
      if (label) label.textContent = text;
      if (icon) icon.textContent = playing ? 'Ⅱ' : '▷';
      button.setAttribute('aria-label', text);
      button.setAttribute('aria-pressed', String(playing));
    }
    function loadSource() {
      if (video.getAttribute('src')) return;
      const source = smallScreen.matches && video.dataset.smallSrc ? video.dataset.smallSrc : video.dataset.src;
      if (!source) throw new Error('Missing film source');
      video.muted = true; // Set before assigning the source for inline mobile playback.
      video.preload = 'auto';
      video.src = source;
    }
    function pause() {
      generation++;
      video.pause();
      sync();
    }
    async function update() {
      if (!wantsPlayback()) { pause(); return; }
      if (blocked || pending || (!video.paused && !video.ended)) return;
      const request = ++generation;
      pending = true;
      try {
        loadSource();
        await video.play();
        if (disposed || request !== generation || !wantsPlayback()) video.pause();
      } catch (error) {
        // Expected AbortErrors occur when a scroll/tab change cancels play().
        // A policy/network rejection leaves an honest click-to-play fallback.
        if (request === generation && wantsPlayback() && error?.name !== 'AbortError') blocked = true;
      } finally {
        pending = false;
        sync();
        if (!disposed && request !== generation && wantsPlayback() && !blocked && video.paused) {
          requestAnimationFrame(update);
        }
      }
    }
    function clicked() {
      if (pending || (!video.paused && !video.ended)) {
        choice = false;
        pause();
      } else {
        choice = true;
        blocked = false;
        visible = true; // A deliberate click may be on the caption at the viewport edge.
        update();
      }
    }
    function mediaError() {
      blocked = true;
      sync();
    }
    function rectVisible() {
      const r = video.getBoundingClientRect();
      return r.width > 0 && r.height > 0 && r.bottom > 0 && r.top < innerHeight && r.right > 0 && r.left < innerWidth;
    }
    function viewportCheck() { visible = rectVisible(); update(); }
    function policyChanged() { blocked = false; update(); }

    button.addEventListener('click', clicked);
    ['play', 'playing', 'pause', 'ended'].forEach(event => video.addEventListener(event, sync));
    video.addEventListener('error', mediaError);
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(entries => {
        for (const entry of entries) {
          visible = entry.isIntersecting && entry.intersectionRatio >= 0.05;
          update();
        }
      }, {threshold: [0, 0.05]});
      observer.observe(video);
    } else {
      window.addEventListener('scroll', viewportCheck, {passive: true});
      window.addEventListener('resize', viewportCheck, {passive: true});
    }
    const player = {
      root: video.closest('[data-fp-home-media]'),
      update,
      policyChanged,
      dispose() {
        disposed = true;
        pause();
        observer?.disconnect();
        window.removeEventListener('scroll', viewportCheck);
        window.removeEventListener('resize', viewportCheck);
        button.removeEventListener('click', clicked);
        ['play', 'playing', 'pause', 'ended'].forEach(event => video.removeEventListener(event, sync));
        video.removeEventListener('error', mediaError);
        initialized.delete(video);
        players.delete(player);
      }
    };
    players.add(player);
    if (!posterReady) {
      const poster = new Image();
      poster.fetchPriority = 'high';
      poster.onload = poster.onerror = () => requestAnimationFrame(() => {
        posterReady = true;
        update();
      });
      poster.src = video.poster;
    }
    sync();
    viewportCheck();
  }
  function start(scope = document) {
    const homes = [...scope.querySelectorAll('[data-fp-home-media]')];
    if (scope.matches?.('[data-fp-home-media]')) homes.unshift(scope);
    for (const home of homes) {
      for (const button of home.querySelectorAll('[data-fpm-auto-toggle]')) {
        const video = [...home.querySelectorAll('[data-fpm-video]')].find(v => v.dataset.fpmVideo === button.dataset.fpmAutoToggle);
        if (video) attach(video, button);
      }
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => start(), {once: true});
  else start();
  document.addEventListener('visibilitychange', () => players.forEach(p => p.update()));
  window.addEventListener('pagehide', () => { pageActive = false; players.forEach(p => p.update()); });
  window.addEventListener('pageshow', () => { pageActive = true; players.forEach(p => p.update()); });
  motion.addEventListener?.('change', () => players.forEach(p => p.policyChanged()));
  connection?.addEventListener?.('change', () => players.forEach(p => p.policyChanged()));
  document.addEventListener('shopify:section:load', event => start(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const p of [...players]) if (p.root && (event.target === p.root || event.target.contains(p.root))) p.dispose();
  });
})();


